/**
 * Momenten omzetten naar wat de kaart toont.
 *
 * De backend levert alles in UTC (contract §1); de frontend zet om naar
 * Europe/Amsterdam. Die zone staat hier hard, niet op de zone van de
 * browser: JP werkt in Nederland en een kaart die op een reis-laptop
 * andere tijden toont is een bug, geen dienst.
 */

export const ZONE = "Europe/Amsterdam";

/**
 * De Nederlandse dag- en maandnamen, hard.
 *
 * Waarom niet `toLocaleDateString("nl-NL", { weekday: "long" })`: dat
 * werkt alleen zolang de browser de Nederlandse locale-gegevens bij
 * zich draagt. Doet hij dat niet, dan valt hij terug op de taal van het
 * systeem en staat er "Tuesday" of een half vertaalde vorm boven
 * Vandaag. Twee dingen gingen daar mis (JP, punt 8 van 22-09): de kop
 * gebruikte de tijdzone van de browser in plaats van Amsterdam — rond
 * middernacht noemde hij dan de verkeerde dag — en elk scherm rekende
 * zijn eigen datum uit. Nu is er één helper en één tabel.
 */
const DAGEN = [
  "zondag",
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
] as const;

const MAANDEN = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
] as const;

/** Dag, maand en jaar zoals ze in Amsterdam op de klok staan. */
function amsterdamseDelen(moment: Date): {
  dag: number;
  weekdag: number;
  maand: number;
  jaar: number;
} {
  const lokaal = inZone(moment);
  return {
    dag: lokaal.getDate(),
    weekdag: lokaal.getDay(),
    maand: lokaal.getMonth(),
    jaar: lokaal.getFullYear(),
  };
}

/** "dinsdag 22 september" — altijd Nederlands, altijd Amsterdam. */
export function langeDatum(moment: Date = new Date()): string {
  const { dag, weekdag, maand } = amsterdamseDelen(moment);
  return `${DAGEN[weekdag]} ${dag} ${MAANDEN[maand]}`;
}

/** "september 2026" voor de periodekop van Retouren en Reviews. */
export function maandJaar(moment: Date = new Date()): string {
  const { maand, jaar } = amsterdamseDelen(moment);
  return `${MAANDEN[maand]} ${jaar}`;
}

function inZone(moment: Date): Date {
  // Truc zonder extra afhankelijkheid: formatteer naar de zone en lees
  // het resultaat terug als lokale datum. Alleen voor dag-vergelijking.
  return new Date(moment.toLocaleString("en-US", { timeZone: ZONE }));
}

/** "09:04" — altijd Amsterdamse klok. */
export function klok(iso: string): string {
  return new Date(iso).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: ZONE,
  });
}

/** "maandag 21 september" voor de datumscheiding, "Vandaag" bij vandaag. */
export function dagkop(iso: string, nu: Date = new Date()): string {
  const dag = inZone(new Date(iso));
  const vandaag = inZone(nu);
  const gisteren = new Date(vandaag);
  gisteren.setDate(gisteren.getDate() - 1);
  if (zelfdeDag(dag, vandaag)) return "Vandaag";
  if (zelfdeDag(dag, gisteren)) return "Gisteren";
  return langeDatum(new Date(iso));
}

/** Sleutel om kaarten per dag te groeperen: "2026-09-21" in Amsterdam. */
export function dagsleutel(iso: string): string {
  const dag = inZone(new Date(iso));
  const maand = String(dag.getMonth() + 1).padStart(2, "0");
  const dagnr = String(dag.getDate()).padStart(2, "0");
  return `${dag.getFullYear()}-${maand}-${dagnr}`;
}

/** Wat rechtsboven op de kaart staat: "09:04" vandaag, anders "za 19 sep". */
export function kaarttijd(iso: string, nu: Date = new Date()): string {
  const dag = inZone(new Date(iso));
  if (zelfdeDag(dag, inZone(nu))) return klok(iso);
  return new Date(iso).toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: ZONE,
  });
}

/** "17 sep 2026, 10:41" voor de feitenrijen van De zaak. */
export function datumTijd(iso: string): string {
  const datum = new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: ZONE,
  });
  return `${datum}, ${klok(iso)}`;
}

/** "17 sep 2026" zonder klok. */
export function datum(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: ZONE,
  });
}

/**
 * Centen naar "€ 12,95".
 *
 * De harde spatie blijft bewust weg: een schermtoets die op "€ 12,95"
 * zoekt faalt anders op niets (les 15-09). ``toLocaleString`` met
 * ``currency`` zet er wél een in, dus dit rekent zelf.
 */
export function euro(cents: number): string {
  const teken = cents < 0 ? "-" : "";
  const heel = Math.floor(Math.abs(cents) / 100);
  const rest = String(Math.abs(cents) % 100).padStart(2, "0");
  const heelTekst = heel.toLocaleString("nl-NL").replace(/\u00a0/g, ".");
  return `€ ${teken}${heelTekst},${rest}`;
}

function zelfdeDag(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
